import { Component, effect, input, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjChartModule } from '@grapecity/wijmo.angular2.chart'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'

import { BaseComponent, Grid } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { GridColumnGroupComponent } from '../../shared/reusable-components/grid-column-group/grid-column-group.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { bindPurchasedMaterialTab, PurchasedMaterialFilters, purchasedMaterialScopeKey } from './purchased-material-filters'

interface PriceChange {
    description: string
    category: string
    unitFamily: string
    startLines: number
    endLines: number
    startMedian: number
    endMedian: number
    percentChange: number
}

interface PriceIndex {
    category: string
    month: string
    index: number
    itemCount: number
}

interface PriceHistoryRow {
    month: string
    unitFamily: string
    vendor: string
    lineCount: number
    median: number
    min: number
    max: number
    vendorCount: number
}

interface PriceSuggestion {
    description: string
    lineCount: number
}

interface PriceChart {
    points: Record<string, string | number>[]
    series: { binding: string; name: string }[]
}

@Component({
    selector: 'app-purchased-materials-prices',
    templateUrl: './purchased-materials-prices.component.html',
    standalone: true,
    imports: [
        FormsModule,
        WjChartModule,
        WjGridModule,
        WjGridFilterModule,
        ActionToolbarCount,
        GridColumnGroupComponent,
        GridExportComponent,
        GridSelectionCalculationComponent,
    ],
})
export class PurchasedMaterialsPricesComponent extends BaseComponent {
    filters = input.required<PurchasedMaterialFilters>()
    active = input(false)
    description = input('')

    changeGrid: Grid<PriceChange>
    indexGrid: Grid<PriceIndex>
    historyGrid: Grid<PriceHistoryRow>
    suggestionGrid: Grid<PriceSuggestion>

    exclusionNote = ''
    changeNote = ''
    indexNote = ''
    historyNote = ''
    finderText = ''
    byVendor = false
    chosen = signal('')
    indexChart: PriceChart = this.emptyChart()
    historyChart: PriceChart = this.emptyChart()

    private finder = signal('')
    private familyChart: PriceChart = this.emptyChart()
    private vendorChart: PriceChart = this.emptyChart()
    private familyRows: PriceHistoryRow[] = []
    private vendorRows: PriceHistoryRow[] = []
    private appliedDescription = ''

    constructor() {
        super()
        this.changeGrid = new Grid<PriceChange>(this.services).wrapColumnHeaders()
        this.indexGrid = new Grid<PriceIndex>(this.services).wrapColumnHeaders()
        this.historyGrid = new Grid<PriceHistoryRow>(this.services).wrapColumnHeaders()
        this.suggestionGrid = new Grid<PriceSuggestion>(this.services).wrapColumnHeaders()

        bindPurchasedMaterialTab(
            this.filters,
            this.active,
            filters => purchasedMaterialScopeKey(filters, false),
            () => this.clearSummary(),
            filters => this.loadSummary(filters),
        )

        effect(() => {
            const search = this.filters().search
            this.finderText = search
            this.finder.set(search)
        })

        effect(() => {
            const incoming = this.description()
            if (!incoming || incoming === this.appliedDescription) return
            this.appliedDescription = incoming
            this.finderText = incoming
            this.finder.set(incoming)
            this.chosen.set(incoming)
        })

        let loadedKey: string | null = null
        let loadedScope: string | null = null
        effect(() => {
            const filters = this.filters()
            const active = this.active()
            const description = this.chosen()
            const search = this.finder()
            const scope = purchasedMaterialScopeKey(filters, false) + '|' + description
            const key = scope + '|' + search
            if (loadedScope !== null && loadedScope !== scope) {
                this.clearHistory()
                loadedKey = null
                loadedScope = null
            }
            if (!active || loadedKey === key) return
            const searchOnly = loadedScope === scope
            loadedKey = key
            loadedScope = scope
            if (!searchOnly) this.clearHistory()
            this.loadHistory(filters, description, search)
        })
    }

    findDescriptions() {
        this.finder.set((this.finderText ?? '').trim())
    }

    showSuggestion() {
        const row = this.suggestionGrid.selectedItem as PriceSuggestion | null
        if (!row?.description) return
        this.chosen.set(row.description)
    }

    showHistory() {
        this.historyChart = this.byVendor ? this.vendorChart : this.familyChart
        this.historyGrid.items = this.byVendor ? this.vendorRows : this.familyRows
    }

    vendorChartIsCapped() {
        return this.byVendor && new Set(this.vendorRows.map(row => row.vendor)).size > 8
    }

    private clearSummary() {
        this.exclusionNote = ''
        this.changeNote = ''
        this.indexNote = ''
        this.changeGrid.items = []
        this.indexGrid.items = []
        this.indexChart = this.emptyChart()
    }

    private clearHistory() {
        this.historyNote = ''
        this.familyChart = this.emptyChart()
        this.vendorChart = this.emptyChart()
        this.familyRows = []
        this.vendorRows = []
        this.historyChart = this.emptyChart()
        this.historyGrid.items = []
        this.suggestionGrid.items = []
    }

    private loadSummary(filters: PurchasedMaterialFilters) {
        this.cancelableRequest(
            'prices',
            true,
            this.services.data
                .load(true, true, this.services.data.post('/api/PurchasedMaterials/GetPrices', filters))
                .subscribe({
                    next: result => {
                        const summary = result[0]
                        this.exclusionNote = summary.exclusionNote
                        this.changeNote = summary.changeNote
                        this.indexNote = summary.indexNote
                        this.changeGrid.items = summary.changes
                        this.indexGrid.items = summary.indexRows
                        this.indexChart = summary.indexChart ?? this.emptyChart()
                    },
                }),
        )
    }

    private loadHistory(filters: PurchasedMaterialFilters, description: string, search: string) {
        this.cancelableRequest(
            'priceHistory',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.post('/api/PurchasedMaterials/GetPriceHistory', {
                        ...filters,
                        search,
                        description,
                    }),
                )
                .subscribe({
                    next: result => {
                        const history = result[0]
                        this.historyNote = history.exclusionNote
                        this.familyChart = history.familyChart ?? this.emptyChart()
                        this.vendorChart = history.vendorChart ?? this.emptyChart()
                        this.familyRows = history.familyRows ?? []
                        this.vendorRows = history.vendorRows ?? []
                        this.suggestionGrid.items = history.suggestions ?? []
                        this.showHistory()
                    },
                }),
        )
    }

    private emptyChart(): PriceChart {
        return { points: [], series: [] }
    }
}
